import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  CardContent, 
  Typography,
  Button,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Divider,
  IconButton,
  FormHelperText,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  Save as SaveIcon,
  Close as CloseIcon,
  LocationCity as LocationIcon
} from '@mui/icons-material';
import { getEstadosCidades } from '../services/api';

const Settings = ({ onClose, settings, onSaveSettings, standalone = false }) => {
  const [formData, setFormData] = useState({
    defaultCity: '',
    defaultState: '',
    defaultCountry: 'Brasil',
    ...settings
  });

  const [estadosCidades, setEstadosCidades] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const [cidadesDoEstado, setCidadesDoEstado] = useState([]);

  // Handler para tecla ESC fechar o modal
  const handleKeyDown = useCallback((event) => {
    if (!standalone && event.key === 'Escape' && onClose) {
      onClose();
    }
  }, [standalone, onClose]);

  // Adicionar event listener para tecla ESC
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    
    // Cleanup: remover event listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Carregar a lista de estados e cidades
  useEffect(() => {
    const fetchEstadosCidades = async () => {
      setLoadingData(true);
      setErrorData(null);
      try {
        const response = await getEstadosCidades();
        if (response && response.success) {
          setEstadosCidades(response.data.estados || []);
          
          // Se já temos um estado selecionado, carrega suas cidades
          if (formData.defaultState) {
            const estadoSelecionado = response.data.estados.find(
              estado => estado.sigla === formData.defaultState
            );
            if (estadoSelecionado) {
              setCidadesDoEstado(estadoSelecionado.cidades);
            }
          }
        } else {
          setErrorData(response?.error || 'Erro ao carregar dados');
        }
      } catch (error) {
        setErrorData('Falha ao carregar lista de estados e cidades');
        console.error('Erro ao carregar estados e cidades:', error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchEstadosCidades();
  }, [formData.defaultState]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'defaultState') {
      // Quando muda o estado, precisamos atualizar a lista de cidades
      const estadoSelecionado = estadosCidades.find(estado => estado.sigla === value);
      
      setFormData({
        ...formData,
        defaultState: value,
        defaultCity: '' // Resetar a cidade quando muda o estado
      });
      
      // Atualizar a lista de cidades para o estado selecionado
      if (estadoSelecionado) {
        setCidadesDoEstado(estadoSelecionado.cidades);
      } else {
        setCidadesDoEstado([]);
      }
    } else {
      // Para outros campos, apenas atualizar o valor
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Sempre usa Brasil como país
    const finalData = {
      ...formData,
      defaultCountry: 'Brasil'
    };
    onSaveSettings(finalData);
    if (onClose) onClose();
  };
  
  const settingsForm = (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <LocationIcon sx={{ mr: 1 }} />
          Localização Padrão
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Defina uma cidade padrão para ajudar no preenchimento automático de endereços
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        {loadingData ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <CircularProgress size={30} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Carregando estados e cidades...
            </Typography>
          </Box>
        ) : errorData ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorData}
          </Alert>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                <InputLabel id="state-select-label">Estado</InputLabel>
                <Select
                  labelId="state-select-label"
                  id="defaultState"
                  name="defaultState"
                  value={formData.defaultState}
                  onChange={handleInputChange}
                  label="Estado"
                >
                  <MenuItem value="">
                    <em>Selecione um estado</em>
                  </MenuItem>
                  {estadosCidades.map(estado => (
                    <MenuItem key={estado.sigla} value={estado.sigla}>
                      {estado.nome} ({estado.sigla})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Selecione o estado padrão para endereços</FormHelperText>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" sx={{ mb: 2 }} disabled={!formData.defaultState}>
                <InputLabel id="city-select-label">Cidade</InputLabel>
                <Select
                  labelId="city-select-label"
                  id="defaultCity"
                  name="defaultCity"
                  value={formData.defaultCity}
                  onChange={handleInputChange}
                  label="Cidade"
                >
                  <MenuItem value="">
                    <em>Selecione uma cidade</em>
                  </MenuItem>
                  {cidadesDoEstado.map(cidade => (
                    <MenuItem key={cidade} value={cidade}>
                      {cidade}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {!formData.defaultState 
                    ? 'Selecione primeiro um estado' 
                    : 'Selecione a cidade padrão para endereços'}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: standalone ? 'flex-end' : 'space-between', mt: 3 }}>
        {onClose && (
          <Button 
            variant="outlined" 
            onClick={onClose}
            startIcon={<CloseIcon />}
          >
            Cancelar
          </Button>
        )}
        <Button 
          type="submit" 
          variant="contained" 
          color="primary"
          startIcon={<SaveIcon />}
        >
          Salvar
        </Button>
      </Box>
    </Box>
  );

  // Se estiver no modo standalone, retornar apenas o formulário em um Card
  if (standalone) {
    return (
      <Card elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <CardContent sx={{ p: 3 }}>
          {settingsForm}
        </CardContent>
      </Card>
    );
  }

  // Caso contrário, envolver em um modal
  return (
    <Box 
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        p: 3
      }}
    >
      <Card 
        sx={{ 
          maxWidth: 600, 
          width: '100%', 
          maxHeight: '90vh',
          overflow: 'auto',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
          <Typography variant="h6">Configurações</Typography>
          <IconButton 
            onClick={onClose} 
            color="inherit" 
            aria-label="fechar"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <CardContent sx={{ p: 3 }}>
          {settingsForm}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Settings;